import React from 'react';
import PropTypes from 'prop-types';
import Seo from '../components/Seo';
import Button from '../components/elements/Button';
import RelatedPages from '../components/sections/partials/RelatedPages';
import { useLanguage } from '../contexts/LanguageContext';
import { seoContent } from '../seo/content';
import { contentPageJsonLd } from '../seo/schema';
import useIsMobile from '../utils/useIsMobile';

/**
 * One view for every long-form page (Moodle agent, WhatsApp agent, agentic
 * pipelines, about).
 *
 * The view owns no copy: it takes a route key, reads
 * `seoContent[lang].pages[key]` from src/seo/content.js and renders it. Adding a
 * page means adding a key to src/seo/routes.js, its copy to content.js and one
 * line to VIEWS in src/App.js.
 *
 * `todos` is internal editorial bookkeeping and is never rendered.
 */

const UI = {
  en: { faq: 'Frequently asked questions', email: 'Send an Email' },
  es: { faq: 'Preguntas frecuentes', email: 'Enviar Email' },
};

/** Prefilled WhatsApp opener per page, so the first message already has context. */
const WHATSAPP_TOPIC = {
  moodleAgent: {
    en: 'Hello, I want to know more about an AI agent for my Moodle course.',
    es: 'Hola, quiero saber más sobre un agente de IA para mi curso en Moodle.',
  },
  whatsappAgent: {
    en: 'Hello, I want to know more about a WhatsApp AI agent.',
    es: 'Hola, quiero saber más sobre un agente de IA para WhatsApp.',
  },
  agenticPipelines: {
    en: 'Hello, I want to know more about agentic pipelines.',
    es: 'Hola, quiero saber más sobre los pipelines de agentes.',
  },
  about: {
    en: 'Hello, I want to know more about working with E-comienza.',
    es: 'Hola, quiero saber más sobre trabajar con E-comienza.',
  },
};

const WHATSAPP_NUMBER = '19544708414';
const CONTACT_EMAIL = 'info@e-comienza.com';

/** Turn a heading into a stable, language-specific anchor id. */
const slugifyHeading = (routeKey, index) => `${routeKey}-section-${index + 1}`;

const propTypes = {
  /** Route key: also the key into seoContent[lang].pages. */
  routeKey: PropTypes.string.isRequired,
};

const ContentPage = ({ routeKey }) => {
  const { lang } = useLanguage();
  const isMobile = useIsMobile();

  const content = seoContent[lang] || seoContent.en;
  const page = content.pages[routeKey];
  const t = UI[lang] || UI.en;

  const topic = (WHATSAPP_TOPIC[routeKey] || WHATSAPP_TOPIC.about)[lang] ||
    (WHATSAPP_TOPIC[routeKey] || WHATSAPP_TOPIC.about).en;
  const waQuery = `phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(topic)}`;
  const waUrl = isMobile
    ? `whatsapp://send?${waQuery}`
    : `https://web.whatsapp.com/send?${waQuery}`;

  return (
    <>
      <Seo
        title={page.title}
        description={page.description}
        routeKey={routeKey}
        jsonLd={contentPageJsonLd(routeKey, page, lang)}
      />

      <article className="section content-page">
        <div className="container">
          <div className="section-inner">
            <header className="content-page-header">
              <h1 className="mt-0 mb-16">{page.h1}</h1>
              <p className="content-page-intro mb-0">{page.intro}</p>
            </header>

            {page.sections.map((section, index) => (
              <section
                key={section.heading}
                className="content-block"
                aria-labelledby={slugifyHeading(routeKey, index)}
              >
                <h2 id={slugifyHeading(routeKey, index)} className="mt-0 mb-16">
                  {section.heading}
                </h2>
                {section.body.map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </section>
            ))}

            <RelatedPages from={routeKey} />

            {/* The questions and the answers are plain text in the DOM: nothing is
                hidden behind JavaScript, so crawlers, screen readers and the
                FAQPage markup all see the same thing. */}
            <section className="content-faq" aria-labelledby={`${routeKey}-faq`}>
              <h2 id={`${routeKey}-faq`} className="mt-0 mb-16">
                {t.faq}
              </h2>
              <div className="faq-list">
                {page.faqs.map((faq) => (
                  <div className="faq-item" key={faq.question}>
                    <h3 className="mt-0 mb-8">{faq.question}</h3>
                    <p className="mb-0">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>

      <section className="cta section cta-overhaul has-top-divider">
        <div className="container">
          <div className="cta-inner section-inner">
            <h2 className="mt-0 mb-0">{page.cta.heading}</h2>
            <div className="content-cta-actions">
              <Button tag="a" color="primary" href={waUrl} target="_blank" rel="noopener noreferrer">
                {page.cta.button}
              </Button>
              <a className="button button-ghost" href={`mailto:${CONTACT_EMAIL}`}>
                {t.email}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

ContentPage.propTypes = propTypes;

export default ContentPage;
